import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { VaultStoreService } from '@app/root-store/vault-store/vault-store.service';
import { AddVaultDialogComponent } from '@app/shared/component/add-vault-dialog/add-vault-dialog.component';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { DocumentService, UserService } from '@app/shared/services';
import { ShareService } from '@app/shared/services/vault/share.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
import { SharedCheckboxComponent } from '@app/shared/component/shared-checkbox/shared-checkbox.component';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TruncateStrPipe } from '@app/shared/pipes/truncate-str.pipe';
import { MatMenuModule } from '@angular/material/menu';
import { FilterListPipe } from '@app/shared/pipes/filter-list.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
@AutoUnsubscribe()
@Component({
    selector: 'individual-user-member',
    templateUrl: './individual-user-member.component.html',
    styleUrls: ['./individual-user-member.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:[
    SharedCheckboxComponent,
      CommonModule,
      MatTableModule,
      FormsModule,
      MatFormFieldModule,
      MatInputModule,
      MatTooltipModule,
      MatSelectModule,
      MatAutocompleteModule,
      TruncateStrPipe,
      MatMenuModule,
      FilterListPipe,
      MatButtonModule,
      MatProgressSpinnerModule,
      MatIconModule
    ]
})
export class IndividualUserMemberComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userMembers: Array<any> | undefined;
  @Input() unlinkedMxTemplates: any[] = [];
  @Input() updated_at;
  @Input() isFromDiscovery?: boolean = false;
  @Input() selectedItemsArray?: Array<any> = [];
  @Output() someEvent = new EventEmitter<string | any>();
  @Output() selectedItemsEmitter = new EventEmitter<Array<any>>();

  screenWidth;
  allSelected = false;
  private subscription = new Subscription();
  private dialog = inject(MatDialog);
  private toastr = inject(ToastrService);
  private userService = inject(UserService);
  private cd = inject(ChangeDetectorRef);
  private vaultService = inject(VaultStoreService);
  private shareService = inject(ShareService);
  private documentService = inject(DocumentService);
  vault_list: any;
  ai_suggested_item: any;
  vaultName: any;
  templates: any[] = [];
  templatesList: any;
  vaultId: any;
  stateObject: any;
  activeVault: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unlinkedMxTemplates']) {
      this.applyTemplatesFromInput();
      this.updateTemplatesForUserMembers();
      this.populateFilteredTemplatesForOpenDropdowns();
      this.cd.detectChanges();
    }
  }

  applyTemplatesFromInput(): void {
    this.templates = Array.isArray(this.unlinkedMxTemplates) ? [...this.unlinkedMxTemplates] : [];
  }

  ngOnInit(): void {
    this.applyTemplatesFromInput();
    this.screenWidth = window.innerWidth;
    this.subscription.add(
      this.vaultService.vaultObject.subscribe((state) => {
        this.stateObject = state;
        this.vaultId = state?.vaultId;
        this.vault_list = state?.vaults;
        this.templatesList = state?.forms;
        // Update AI suggestions when vaults are loaded
        this.updateAISuggestions();
      })
    );
    this.screenWidth = window.innerWidth;
    this.userMembers = this.userMembers.map((item) => ({
      ...item,
      name: item?.guid ? (item?.nickname ?? item.name) : (item?.name ?? item?.official_name),
      status: item.user_account_status,
      action: item.user_account_status == 'null' ? 'create' : 'existing',
      template: this.getFilteredTemplate(item),
      showTemplateDropdown: false,
      notInUseAction: 'notInUse',
      templateSearchText: '',
      filteredTemplates: [],
      vaultSearchText: '',
      account_balance: item?.guid 
        ? (item?.balance != null ? Number(item.balance) : 0)
        : (item?.current_balance != null ? Number(item.current_balance) : 0),
      account_number: (item?.account_number ?? item?.mask ?? '').slice(-4),
      account_number_hidden: true,
      ...(!item?.id && item?.account_id && { id: item.account_id}),
    }));
  }
  private populateFilteredTemplatesForOpenDropdowns(): void {
    if (!this.userMembers || !this.templates) return;
    this.userMembers.forEach((member) => {
      if (member.showTemplateDropdown) {
        member.filteredTemplates = [...this.templates];
      }
    });
  }

  /** Form type IDs for financial account templates (getFormsTypeUser) */
  private readonly FINANCIAL_ACCOUNT_FORM_IDS = [437, 2004, 2005];

  private getFinancialAccountFormIds(): number[] {
    return this.FINANCIAL_ACCOUNT_FORM_IDS;
  }

  private updateTemplatesForUserMembers(): void {
    if (this.userMembers) {
      this.userMembers = this.userMembers.map((item) => ({
        ...item,
        template: this.getFilteredTemplate(item),
      }));
      this.cd.detectChanges();
    }
  }

  getFilteredTemplate(item: any): any {
    if (!this.templates || !item.vaultUserForm?.vault_form_id) {
      return null;
    }

    // Filter templates based on vault_form_id
    const filteredTemplate = this.templates.find((template) => template.vault_form_id === item.vaultUserForm.vault_form_id);

    return filteredTemplate || null;
  }

  getTemplateType(item: any): string {
    if (!this.templatesList || !item.vaultUserForm?.vault_form_id) {
      return null;
    }

    // Filter templatesList where id matches vault_form_id
    const matchedTemplate = this.templatesList.find((template) => template.id === item.vaultUserForm.vault_form_id);
    return item?.name || matchedTemplate?.type || null;
  }
  removelinkedForm(item) {
    const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Are you sure you want to remove file link ?',
        materialIcon: 'warning',
        text: ``,
        proceedTitle: 'Remove Link',
      },
    });
    this.subscription.add(
      sharedDialogRef.afterClosed().subscribe((confirmation) => {
        if (confirmation) {
          if(item.guid){
            let fData = { guid: item.guid, vault_user_form_id: null };
            this.subscription.add(
              this.userService.updateUserLinkedForm(fData).subscribe((val) => {
                this.toastr.success('Form removed successfully.');
                this.someEvent.next('somePhone');
              })
            );
          } else if(item.account_id){
            let fData = { account_id: item.account_id, vault_user_form_id: null };
            this.subscription.add(
              this.userService.updatePlaidAccountLinkForm(fData).subscribe((val) => {
                this.toastr.success('Form removed successfully.');
                this.someEvent.next('somePhone');
              })
            );
          }
        }
      })
    );
  }
  linkedForm(item) {
    this.someEvent.emit({
      linkForm: true,
      item,
    });
  }
  openForm(file) {
    this.someEvent.emit({
      openForm: true,
      file,
    });
  }

  selectAll(ev) {
    this.userMembers = this.userMembers.map((member) => {
      return {
        ...member,
        checked: member.status === 'in use' ? ev.checked : member.checked,
      };
    });
    this.allSelected = this.userMembers.every((member) => member.checked);
    this.handleSelectedItemsEmitter();
    this.cd.detectChanges();
  }

  selectOne(ev, item) {
    if (!this.userMembers) return;
    this.userMembers = [...this.userMembers].map((member) => (item.id === member.id ? { ...member, checked: ev.checked } : member));

    this.allSelected = this.userMembers.every((member) => member.checked);
    this.handleSelectedItemsEmitter();
    this.cd.detectChanges();
  }
  onStatusChange(item) {
    // Handle status change logic here
    this.cd.detectChanges();
    if (item.status === 'in use') {
      item.activeVault = true;
      // When status becomes "in use", template editing must be hidden for all rows.
      this.userMembers?.forEach((member) => {
        member.showTemplateDropdown = false;
        member.templateEditing = false;
      });
    } else {
      item.activeVault = false;
      if(item.guid){
        this.updatemxMemberAccountStatus(item);
      } else if(item.account_id){
        this.updateUserAccountPlaidStatus(item);
      }
    }
    this.cd.detectChanges();
  }
  createUserMemberAccountSingle(selectedAccounts, vault) {
    return new Promise((resolve) => {
      this.subscription.add(
        this.userService
          .createUserMemberAccountSingle({
            user_id: this.stateObject?.userId,
            user_account_guid: selectedAccounts.guid,
            vault_id: vault.id,
            action: 'create',
          })
          .subscribe((res) => {
            resolve(res);
            this.someEvent.emit({
              somePhone: true,
            });
          })
      );
    });
  }

  createUserPlaidAccountSingle(selectedAccounts, vault) {
    return new Promise((resolve) => {
      this.subscription.add(
        this.userService
          .createUserPlaidAccountSingle({
            user_id: this.stateObject?.userId,
            account_id: selectedAccounts.account_id,
            vault_id: vault.id,
            action: 'create',
          })
          .subscribe((res) => {
            resolve(res);
          })
      );
    });
  }

  getStatusActionHeader(): string {
    // Live Data Feeds: always allow Link Credential dropdown
    if (!this.isFromDiscovery) return 'Action';
    // Discovery: show Action only when any account has null status
    const hasNullStatus = this.userMembers?.some((item) => item.user_account_status === null);
    return hasNullStatus ? 'Action' : 'Status';
  }

  showSaveLoading(index) {
    this.userMembers[index]['saveLoading'] = true;
    setTimeout(() => {
      this.userMembers[index]['saveLoading'] = false;
    }, 500);
  }

  updateVault(element, vault) {
    const index = this.userMembers.findIndex((u) => u.id === element.id);
    if (index >= 0) {
      this.userMembers[index]['loading'] = true;
      this.cd.detectChanges();
    }
    return new Promise(async (resolve) => {
      if (!element?.vault_user_form_id) {
        let res;
        if(element.guid){
          res = await this.createUserMemberAccountSingle(element, vault);
        } else if(element.account_id){
          res = await this.createUserPlaidAccountSingle(element, vault);
        }
        element.vault_user_form_id = res;
        await this.updateFormName(element);
        if (index >= 0) {
          this.userMembers[index]['loading'] = false;
        }
        this.showSaveLoading(index);
        resolve(true);
      } else {
        this.subscription.add(
          this.documentService
            .updateFormGroupId({
              id: [element.vault_user_form_id],
              vault_id: vault.id,
              vault_user_id: element.user_id,
            })
            .subscribe(async (res) => {
              resolve(res);
              if (index >= 0) {
                this.userMembers[index]['loading'] = false;
              }
              await this.updateFormName(element);
              this.showSaveLoading(index);
            })
        );
      }
    });
  }

  async multiUpdateVault(vault) {
    const promises = [];
    const targets = this.userMembers.filter((item) => item.status === 'in use' && item.checked);
    const hasCreateTargets = targets.some((item) => !item.vault_user_form_id);

    targets.forEach((element) => {
      if (element.status === 'in use') {
        promises.push(this.updateVault(element, vault));
      }
    });

    await Promise.allSettled(promises);
    if (hasCreateTargets) {
      await this.initiateFinancialAccountDataRefreshForUser();
    }
    this.someEvent.emit({
      somePhone: true,
    });
    this.selectAll({ checked: false });
  }

  updateFormName(item, isFromNameChange = false) {
    let findIndex = -1;
    if (isFromNameChange) {
      findIndex = this.userMembers.findIndex((x) => x.id === item.id);
      if (findIndex >= 0) {
        this.userMembers[findIndex]['loading'] = true;
        this.cd.detectChanges();
      }
    }
    return new Promise((resolve) => {
      this.subscription.add(
        this.documentService
          .saveFormNameInDb({
            form_name: item.name,
            id: item.vault_user_form_id,
            form_name_updated_by_user: true,
          })
          .subscribe(() => {
            if (isFromNameChange) {
              this.userMembers[findIndex]['loading'] = false;
              this.showSaveLoading(findIndex);
              this.cd.detectChanges();
            }
            resolve(true);
          })
      );
    });
  }

  async updateFormGroupIdSingle(element, vault) {
    if (this.userMembers.filter((item) => item.status === 'in use' && item.checked).length > 1) {
      await this.multiUpdateVault(vault);
      return;
    }
    const findIndex = this.userMembers.findIndex((x) => x.id === element.id);
    if (findIndex >= 0) {
      this.userMembers[findIndex]['loading'] = true;
    }
    if (!element?.vault_user_form_id) {
      let res;
      if(element.account_id){
        res = await this.createUserPlaidAccountSingle(element, vault);
      } else if(element.guid){
        res = await this.createUserMemberAccountSingle(element, vault);
      }
      element.vault_user_form_id = res;
      await this.updateFormName(element);
      if (findIndex >= 0) {
        this.userMembers[findIndex]['loading'] = false;
      }
      this.showSaveLoading(findIndex);
      await this.initiateFinancialAccountDataRefreshForUser();
    } else {
      return new Promise((resolve) => {
        this.subscription.add(
          this.documentService
            .updateFormGroupId({
              id: [element.vault_user_form_id],
              vault_id: vault.id,
              vault_user_id: element.user_id,
            })
            .subscribe(async (res) => {
              resolve(res);
              await this.updateFormName(element);
              this.someEvent.emit({
                somePhone: true,
              });
              if (findIndex >= 0) {
                this.userMembers[findIndex]['loading'] = false;
              }
              this.showSaveLoading(findIndex);
            })
        );
      });
    }
  }

  handleSelectedItemsEmitter() {
    this.selectedItemsEmitter.emit(this.userMembers);
  }

  openTemplateDropdown(item: any) {
    this.userMembers.forEach((member) => {
      if (member !== item) {
        member.showTemplateDropdown = false;
        member.templateEditing = false;
      }
    });

    item.showTemplateDropdown = !item.showTemplateDropdown;
    if (item.showTemplateDropdown) {
      this.userMembers.forEach((member) => {
        member.templateEditing = member === item;
      });
      this.filterTemplates(item);
    } else {
      item.templateEditing = false;
    }
    this.cd.detectChanges();
  }

  filterTemplates(item: any) {
    const searchTerm = (item.templateSearchText || '').toLowerCase();
    const list = this.templates || [];

    if (!searchTerm) {
      item.filteredTemplates = [...list];
      return;
    }

    item.filteredTemplates = list.filter(
      (template) =>
        (template.type && template.type.toLowerCase().includes(searchTerm)) ||
        (template.type_template && template.type_template.toLowerCase().includes(searchTerm)) ||
        (template.form_name && template.form_name.toLowerCase().includes(searchTerm))
    );
  }

  getAISuggestedVault(item: any) {
    // For demo purposes, let's create an AI suggested vault
    // In a real implementation, this would come from an AI service
    if (this.vault_list && this.vault_list.length > 0) {
      // Find a personal vault as AI suggestion
      const personalVault = this.vault_list.find((vault) => vault.type === 'personal');
      if (personalVault) {
        return {
          ...personalVault,
          id: -1, // Special ID to indicate it's an AI suggestion
          name: personalVault.name + ' (AI Suggested)',
        };
      }
    }
    return null;
  }

  updateAISuggestions() {
    if (this.userMembers && this.vault_list) {
      this.userMembers = this.userMembers.map((item) => ({
        ...item,
        ai_suggested_item: this.getAISuggestedVault(item),
      }));
      this.cd.detectChanges();
    }
  }
  addNewVault(file, vault_details = null) {
    const modalRef = this.dialog.open(AddVaultDialogComponent, {
      panelClass: 'full-window-dialog-2',
      disableClose: true,
      autoFocus: false,
      data: {
        user_id: this.stateObject?.userId,
        fromDiscovery: false,
        vault_suggestion_details: vault_details,
      },
    });
    this.subscription.add(
      modalRef.afterClosed().subscribe((res) => {
        if (res.message) {
          const findIndex = this.userMembers.findIndex((x) => x.id === file.id);
          if (findIndex >= 0) {
            this.vault_list.push(res.data);
            this.userMembers[findIndex]['vault_details'] = res.data;
          }
          // this.saveVault([{ vault_user_form_id: file.vault_user_form_id, vault_id: res.data.id }]);
          // this.store$.dispatch(new vaultStoreActions.GetVaultRequestAction(this.stateObject?.userId, false));
        }
      })
    );
  }
  updatemxMemberAccountStatus(item) {
    this.subscription.add(
      this.userService
        .updatemxMemberAccountStatus({
          user_account_guid: item.guid,
          user_account_status: item.status,
        })
        .subscribe((res) => {
          this.toastr.success('Status updated successfully.');
          this.someEvent.next('somePhone');
        })
    );
  }

  updateUserAccountPlaidStatus(item) {
    this.subscription.add(
      this.userService
        .updateUserAccountPlaidStatus({
          account_id: item.account_id,
          user_account_status: item.status,
        })
        .subscribe((res) => {
          this.toastr.success('Status updated successfully.');
          this.someEvent.next('somePhone');
        })
    );
  }

  private linkExistingTemplateToAccount(item: any): void {
    if (!item?.template?.id) {
      return;
    }

    if (item.guid) {
      // MX account: link to existing vault_user_form_id
      this.subscription.add(
        this.userService
          .updateUserLinkedForm({
            guid: item.guid,
            vault_user_form_id: item.template.id,
            createRolePlayerSuggestions: true,
          })
          .subscribe(() => {
            this.toastr.success('Account linked to existing template.');
            this.someEvent.next('somePhone');
          })
      );
    } else if (item.account_id) {
      // Plaid account: link to existing vault_user_form_id
      this.subscription.add(
        this.userService
          .updatePlaidAccountLinkForm({
            account_id: item.account_id,
            vault_user_form_id: item.template.id,
            createRolePlayerSuggestions: true,
          })
          .subscribe(() => {
            this.toastr.success('Account linked to existing template.');
            this.someEvent.next('somePhone');
          })
      );
    }
  }

  onNotInUseActionChange(item: any, action: 'addNew' | 'selectExisting' | 'notInUse'): void {
    item.notInUseAction = action;
    // Match connect-to-mx lifecycle: only one row should be in "template editing" state at a time.
    this.userMembers?.forEach((member) => {
      member.showTemplateDropdown = false;
      member.templateEditing = false;
    });

    if (action === 'addNew') {
      // Switch row to "in use" and ensure template UI is hidden
      item.status = 'in use';
      item.templateEditing = false;
      this.onStatusChange(item);
    } else if (action === 'selectExisting') {
      // Activate template input only on this row
      item.templateEditing = true;
      item.showTemplateDropdown = false;
    } else {
      // action === 'notInUse' => keep template UI hidden
      item.templateEditing = false;
      item.showTemplateDropdown = false;
    }

    this.cd.detectChanges();
  }

  onTemplateSelectForNotInUse(item: any, template: any): void {
    // Mirror onTemplateSelect behavior from ConnectToMx for basic UI state
    item.template = template;
    item.templateSearchText = template?.form_name || template?.type || '';
    item.templateEditing = false;
    item.showTemplateDropdown = false;

    // When using "Select Existing" for not-in-use rows, immediately link to the chosen template
    // For "Select Existing" on not-in-use rows, immediately link to the chosen template
    if (item.status === 'not in use' && item.notInUseAction === 'selectExisting') {
      this.linkExistingTemplateToAccount(item);
    }
    this.cd.detectChanges();
  }

  onTemplateClickForRow(item: any, template: any): void {
    if (item.status === 'not in use' && item.notInUseAction === 'selectExisting') {
      this.onTemplateSelectForNotInUse(item, template);
    } else {
      item.template = template;
      item.showTemplateDropdown = false;
      item.templateEditing = false;
      this.cd.detectChanges();
    }
  }

  initiateFinancialAccountDataRefreshForUser() {
    return new Promise((resolve) => {
      this.subscription.add(
        this.userService.initiateFinancialAccountDataRefreshForUser().subscribe({
          next: (res) => resolve(res),
          error: () => {
            resolve(undefined);
          },
        })
      );
    });
  }
  
  onNameChange(item, i) {
    // this.userMembers[i].name = item.name;
    this.cd.detectChanges();
  }
  onNameInput(item) {
    const member = this.userMembers.find((m) => m.id === item.id);
    if (member) {
      member.itemEditInputField = true;
      this.cd.detectChanges();
    }
  }
  async onNameBlur(item) {
    const member = this.userMembers.find((m) => m.id === item.id);
    if (member) {
      member.itemEditInputField = false;
      if (item.vault_user_form_id) {
        await this.updateFormName(item, true);
        this.someEvent.emit({
          somePhone: true,
        });
      }
      this.cd.detectChanges();
    }
    this.cd.detectChanges();
  }

  onTemplatePanelOpened(item: any): void {
    item.showTemplateDropdown = true;
    this.filterTemplates(item);
    this.cd.detectChanges();
  }
  
  onTemplatePanelClosed(item: any): void {
    item.showTemplateDropdown = false;
    item.templateEditing = false;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
